// src/components/admin/design/UniversalProductCardPreview.tsx
"use client";

import React, { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Sparkles, Sun, Moon } from "lucide-react";
import {
  resolveProductCardComponent,
  PRODUCT_CARD_VARIANT_LIST,
} from "@/components/store/sections/product-cards";
import { resolveCurrentSubdomain } from "@/components/store/sections/navbar/brandingConfig";

export const DEMO_PREVIEW_PRODUCTS = [
  {
    id: 101,
    name: "Aero Pulse Neon X1",
    slug: "aero-pulse-neon-x1",
    price: 1250,
    compare_price: 1650,
    category_name: "Performance Footwear",
    description: "Ultra-lightweight breathable mesh with high-rebound cushion sole.",
    rating: 4.9,
    stock_quantity: 14,
    primary_image:
      "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=600&auto=format&fit=crop&q=80",
  },
  {
    id: 102,
    name: "Quantum Chrono Luxe",
    slug: "quantum-chrono-luxe",
    price: 2490,
    compare_price: 3200,
    category_name: "Smart Luxury Wear",
    description: "Precision crafted sapphire glass watch with titanium strap.",
    rating: 5.0,
    stock_quantity: 9,
    primary_image:
      "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=600&auto=format&fit=crop&q=80",
  },
  {
    id: 103,
    name: "Nordic Velvet Chair",
    slug: "nordic-velvet-chair",
    price: 4500,
    compare_price: 5800,
    category_name: "Modern Living",
    description: "Ergonomic Scandinavian woodwork with plush velvet upholstery.",
    rating: 4.8,
    stock_quantity: 6,
    primary_image:
      "https://images.unsplash.com/photo-1503602642458-232111445657?w=500&auto=format&fit=crop&q=80",
  },
  {
    id: 104,
    name: "Sonic Pro ANC Headphones",
    slug: "sonic-pro-anc",
    price: 3150,
    compare_price: 4200,
    category_name: "Audio Gear",
    description: "Studio-grade spatial audio with 45-hour battery life.",
    rating: 4.9,
    stock_quantity: 19,
    primary_image:
      "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=600&auto=format&fit=crop&q=80",
  },
  {
    id: 105,
    name: "Botanical Glow Elixir",
    slug: "botanical-glow-elixir",
    price: 980,
    compare_price: 1350,
    category_name: "Organic Skincare",
    description: "Cold-pressed vitamin C serum for radiant hydration.",
    rating: 4.9,
    stock_quantity: 24,
    primary_image:
      "https://images.unsplash.com/photo-1526170375885-4d8ecf77b99f?w=600&auto=format&fit=crop&q=80",
  },
  {
    id: 106,
    name: "Minimalist Leather Tote",
    slug: "minimalist-leather-tote",
    price: 2890,
    compare_price: 3600,
    category_name: "Bags & Wallets",
    description: "Hand-stitched full-grain Italian leather everyday bag.",
    rating: 4.8,
    stock_quantity: 11,
    primary_image:
      "https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=600&auto=format&fit=crop&q=80",
  },
];

export function useDetectedAdminTheme(explicitTheme?: "dark" | "light"): ["dark" | "light", (t: "dark" | "light") => void] {
  const [theme, setTheme] = useState<"dark" | "light">(() => {
    if (explicitTheme === "light" || explicitTheme === "dark") return explicitTheme;
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("admin_theme");
      if (saved === "light" || saved === "dark") return saved;
      if (document.documentElement.classList.contains("dark")) return "dark";
    }
    return "dark";
  });

  useEffect(() => {
    if (explicitTheme === "light" || explicitTheme === "dark") {
      setTheme(explicitTheme);
      return;
    }
    if (typeof window === "undefined") return;

    const syncTheme = () => {
      const saved = localStorage.getItem("admin_theme");
      if (saved === "light" || saved === "dark") {
        setTheme(saved);
      } else {
        setTheme(document.documentElement.classList.contains("dark") ? "dark" : "light");
      }
    };

    syncTheme();
    window.addEventListener("storage", syncTheme);
    window.addEventListener("xylo_preview_theme_change", syncTheme as EventListener);

    const observer = new MutationObserver(syncTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("xylo_preview_theme_change", syncTheme as EventListener);
      observer.disconnect();
    };
  }, [explicitTheme]);

  return [theme, setTheme];
}

export const DESIGN_STUDIO_MODAL_FIX_CSS = `
  /* Fix SectionDesignModal dark/light mode contrast even when OS theme differs from Admin theme */
  .bg-slate-900 h4.text-slate-900 {
    color: #f8fafc !important;
  }
  .bg-white h4.text-slate-900 {
    color: #0f172a !important;
  }
  .bg-slate-900 .divide-slate-200 > :not([hidden]) ~ :not([hidden]) {
    border-color: #1e293b !important;
  }
  .bg-slate-900 .border-slate-200 {
    border-color: #1e293b !important;
  }
  .bg-white .divide-slate-200 > :not([hidden]) ~ :not([hidden]) {
    border-color: #e2e8f0 !important;
  }

  /* Dark Mode Preview Stage Adaptations */
  [data-xylo-preview-theme="dark"] section > div:first-child h2.text-slate-900,
  [data-xylo-preview-theme="dark"] section > div > div > h2.text-slate-900,
  [data-xylo-preview-theme="dark"] section > div > h2.text-slate-900 {
    color: #f8fafc !important;
  }
  [data-xylo-preview-theme="dark"] section > div:first-child.border-slate-200\\/80,
  [data-xylo-preview-theme="dark"] section > div:first-child.border-slate-200 {
    border-color: rgba(51, 65, 85, 0.7) !important;
  }
  [data-xylo-preview-theme="dark"] section > div:first-child button.bg-white.text-slate-700 {
    background-color: #0f172a !important;
    color: #e2e8f0 !important;
    border-color: #334155 !important;
  }

  /* Light Mode Preview Stage Adaptations */
  [data-xylo-preview-theme="light"] section > div:first-child h2.text-slate-900,
  [data-xylo-preview-theme="light"] section > div > div > h2.text-slate-900,
  [data-xylo-preview-theme="light"] section > div > h2.text-slate-900 {
    color: #0f172a !important;
  }
`;

interface UniversalProductCardPreviewProps {
  sectionTitle: string;
  variantId: string;
  primaryColor?: string;
  showTimer?: boolean;
  theme?: "dark" | "light";
  subdomain?: string;
}

export default function UniversalProductCardPreview({
  sectionTitle,
  variantId,
  primaryColor = "#059669",
  showTimer = false,
  theme: propTheme,
  subdomain: propSubdomain,
}: UniversalProductCardPreviewProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeTheme, setActiveTheme] = useDetectedAdminTheme(propTheme);
  const isLight = activeTheme === "light";

  const resolvedSubdomain = resolveCurrentSubdomain(propSubdomain || "nextlook");
  const CardComponent = resolveProductCardComponent(variantId);
  const variantMeta = PRODUCT_CARD_VARIANT_LIST.find((v) => v.id === variantId);

  const handleScroll = (dir: "left" | "right") => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: dir === "left" ? -320 : 320,
        behavior: "smooth",
      });
    }
  };

  return (
    <div
      data-xylo-preview-theme={activeTheme}
      className={`w-full rounded-2xl border p-4 space-y-4 overflow-hidden transition-colors ${
        isLight
          ? "bg-white border-slate-200/90 text-slate-900 shadow-xs"
          : "bg-[#0b1120] border-slate-800/90 text-slate-100 shadow-lg dark"
      }`}
    >
      <style dangerouslySetInnerHTML={{ __html: DESIGN_STUDIO_MODAL_FIX_CSS }} />

      <div
        className={`flex flex-wrap items-center justify-between gap-2 pb-3 border-b ${
          isLight ? "border-slate-200" : "border-slate-800"
        }`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span
            style={{
              color: primaryColor,
              borderColor: `${primaryColor}40`,
              backgroundColor: `${primaryColor}15`,
            }}
            className="px-3 py-1 rounded-full border text-[10px] font-black uppercase font-mono flex items-center gap-1.5 truncate"
          >
            <Sparkles className="w-3 h-3 shrink-0" />
            <span className="truncate">
              {sectionTitle} — {variantMeta?.name || variantId}
            </span>
          </span>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Light / Dark Preview Toggle */}
          <div
            className={`inline-flex items-center p-0.5 rounded-xl border text-[10px] font-bold ${
              isLight
                ? "bg-slate-100 border-slate-200 text-slate-600"
                : "bg-slate-900 border-slate-800 text-slate-400"
            }`}
          >
            <button
              type="button"
              onClick={() => setActiveTheme("light")}
              className={`px-2 py-1 rounded-lg flex items-center gap-1 transition-all cursor-pointer ${
                isLight
                  ? "bg-white text-amber-600 shadow-2xs font-black"
                  : "hover:text-slate-200"
              }`}
              title="Preview in Light Mode"
            >
              <Sun className="w-3 h-3" />
              <span>Light</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTheme("dark")}
              className={`px-2 py-1 rounded-lg flex items-center gap-1 transition-all cursor-pointer ${
                !isLight
                  ? "bg-slate-800 text-emerald-400 shadow-2xs font-black"
                  : "hover:text-slate-900"
              }`}
              title="Preview in Dark Mode"
            >
              <Moon className="w-3 h-3" />
              <span>Dark</span>
            </button>
          </div>

          <span
            className={`text-[10px] font-bold mr-0.5 hidden sm:inline ${
              isLight ? "text-slate-500" : "text-slate-400"
            }`}
          >
            Swipe / Scroll:
          </span>
          <button
            type="button"
            onClick={() => handleScroll("left")}
            className={`w-8 h-8 rounded-full border-2 flex items-center justify-center shadow-xs hover:border-emerald-500 active:scale-95 transition-all cursor-pointer ${
              isLight
                ? "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                : "border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
            }`}
            title="Scroll Left"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => handleScroll("right")}
            className={`w-8 h-8 rounded-full border-2 flex items-center justify-center shadow-xs hover:border-emerald-500 active:scale-95 transition-all cursor-pointer ${
              isLight
                ? "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                : "border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
            }`}
            title="Scroll Right"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Horizontal Scrollable Product Cards Container */}
      <div
        ref={scrollRef}
        className="flex items-stretch gap-4 overflow-x-auto pt-2 pb-4 px-1 [scrollbar-width:thin] scroll-smooth"
      >
        {DEMO_PREVIEW_PRODUCTS.map((prod, idx) => (
          <div key={prod.id} className="w-64 sm:w-72 shrink-0 flex">
            <CardComponent
              product={prod}
              index={idx}
              primaryColor={primaryColor}
              showTimer={showTimer}
              subdomain={resolvedSubdomain}
            />
          </div>
        ))}
      </div>
    </div>
  );
}